import { FileThumbnail, useFilePreview } from '@atlas/ui-components'
import { fileTypeIcon, fileTypeToken, typeMarker } from '@atlas/ui-domain'
import { ProgressBar } from '@atlas/ui-primitives'
import { Download, Trash2, X } from 'lucide-react'

export interface DesignFileTileProps {
	name: string
	contentType?: string
	thumbnailUrl?: string
	/** The local File while it uploads, previewed before any server thumbnail exists. */
	file?: File
	/** Under the name: the file's size, or what the upload is doing. */
	detail?: string
	/** Upload progress in percent, while the file is still transferring. */
	progress?: number
	/** Why an upload failed; nothing was stored. */
	error?: string
	/** Absent until the file is stored. */
	onDownload?: () => void
	/** Offered on a draft to the designer. */
	onDelete?: () => void
	/** Removes a failed upload's tile. */
	onDismiss?: () => void
}

const ICON_BUTTON = 'rounded-ds-sm p-1 hover:bg-ds-background'

/** One design file as a grid tile: its preview across the top, then its name, size and actions. */
export function DesignFileTile({
	name,
	contentType,
	thumbnailUrl,
	file,
	detail,
	progress,
	error,
	onDownload,
	onDelete,
	onDismiss,
}: DesignFileTileProps) {
	const localPreview = useFilePreview(file, contentType ?? '')
	const actions = [
		{ onPress: onDownload, label: 'Download', Icon: Download, tone: 'text-ds-primary' },
		{ onPress: onDelete, label: 'Remove', Icon: Trash2, tone: 'text-ds-text-subtle hover:text-ds-danger-strong' },
		{ onPress: onDismiss, label: 'Dismiss', Icon: X, tone: 'text-ds-text-subtle hover:text-ds-text' },
	]
	return (
		<li className="overflow-hidden rounded-ds-md border border-ds-border bg-ds-surface">
			<FileThumbnail
				name={name}
				thumbnailUrl={thumbnailUrl ?? localPreview ?? undefined}
				size="lg"
				icon={fileTypeIcon(fileTypeToken(contentType, name))}
				marker={typeMarker(contentType ?? '')}
				className="aspect-[4/1] h-auto w-full rounded-none border-0 border-b border-ds-border"
			/>
			<div className="flex items-start gap-2 p-2">
				<span className="flex min-w-0 flex-1 flex-col gap-0.5">
					<span className="truncate text-ds-sm text-ds-text">{name}</span>
					{error ? (
						<span className="text-ds-xs text-ds-danger">{error}</span>
					) : progress !== undefined ? (
						<ProgressBar size="sm" value={progress} aria-label={`Upload progress for ${name}`} />
					) : (
						<span className="text-ds-xs text-ds-text-muted">{detail}</span>
					)}
				</span>
				{actions.map(({ onPress, label, Icon, tone }) =>
					onPress ? (
						<button
							key={label}
							type="button"
							aria-label={`${label} ${name}`}
							onClick={onPress}
							className={`${ICON_BUTTON} ${tone}`}
						>
							<Icon className="h-4 w-4" />
						</button>
					) : null,
				)}
			</div>
		</li>
	)
}

/** The two-column grid the tiles sit in, one column on a narrow screen. */
export const DESIGN_TILE_GRID = 'grid grid-cols-1 gap-3 sm:grid-cols-2'
